import { ReactNode, useEffect } from 'react';
import { lowerCase, startCase } from 'lodash';
import { twMerge } from 'tailwind-merge';

import type { Ryecon } from '@dbt-labs/sourdough';
import {
  Icon,
  Link,
  RyeconActivity,
  RyeconDuotoneInfo,
  RyeconFile,
  RyeconGraphNodes,
  RyeconLineage,
  RyeconModelLayers,
  RyeconNodeQuery,
  RyeconPath,
  RyeconProject,
  RyeconTest,
  Tooltip,
} from '@dbt-labs/sourdough';

import { Lens } from '../dag/InternalDagInterfaces';
import {
  costBuckets,
  materializationTypes,
  modelLayers,
  queryCountBuckets,
  statusLegendTypes,
  testStatuses,
  transformationTypes,
  useDagContext,
} from '../dbt-dag';
import { Subcomponent } from '../utils/subcomponent';
import {
  ColorMap,
  costColors,
  materializationTypeColors,
  modelLayerColors,
  nodeStatusColors,
  queryCountsColors,
  resourceTypeColors,
  resourceTypesInLegend,
  transformationTypeColors,
} from './LensNode/lensConstants';

type LensLegendDisplayProps<TKey extends string> = {
  className?: string | null;
  title: string;
  titleIcon: Ryecon;
  description?: ReactNode;
  colorMap: ColorMap<TKey>;
  options: readonly TKey[];
  /** Optional: Group multiple options with the same color into single legend items */
  groupedLegendItems?: Array<{ label: string; color: string; keys: readonly TKey[] }>;
  /** Displayed after the legend */
  tagline?: string;
  isSelectable: boolean;
};

const LensLegendDisplay = <TKey extends string>({
  title,
  className,
  colorMap,
  options,
  groupedLegendItems,
  tagline,
}: LensLegendDisplayProps<TKey>) => {
  // Use grouped items if provided, otherwise fall back to individual options
  const legendItems =
    groupedLegendItems ||
    options.map((option) => ({
      label: startCase(lowerCase(option)),
      color: colorMap[option]?.legend || '',
      keys: [option],
    }));

  return (
    <div
      className={twMerge(
        'pointer-events-auto flex flex-wrap content-center justify-center align-middle',
        'box-border border border-borderMain bg-bgMain text-fgMain',
        'mx-2 min-h-8 w-fit items-center rounded px-2',
        className,
      )}
    >
      <span className="mr-2 overflow-hidden truncate text-nowrap text-sm has-lens-switcher:hidden">
        <Icon
          ryecon={RyeconGraphNodes}
          size="sm"
          className="mr-1 inline-block align-middle"
        />
        <span className="align-middle">{title}</span>
      </span>
      <span className="space-x-2 px-2">
        {legendItems.map((item, index) => (
          <span
            className="h-8 space-x-1 overflow-hidden whitespace-nowrap align-middle"
            key={groupedLegendItems ? index : item.keys[0]}
          >
            <span className={`inline-block h-2 w-2 ${item.color}`} />
            <span className="select-none overflow-hidden overflow-ellipsis text-xs leading-7">
              {item.label}
            </span>
          </span>
        ))}
      </span>
      {tagline && (
        <span className="ml-2 border-l border-borderMain pl-4 pr-2 text-xs leading-[30px]">
          {tagline}
        </span>
      )}
    </div>
  );
};

export interface LensLegendProps {
  className?: string | null;
  initialLens?: Lens | null;
  onErrorFooterClick?(e: React.MouseEvent<HTMLAnchorElement>): void;
}

export const LensLegends: Record<Lens | 'default', LensLegendDisplayProps<string>> = {
  default: {
    title: 'Resource type',
    titleIcon: RyeconLineage,
    description: 'Resources organized by resource type',
    colorMap: resourceTypeColors,
    options: resourceTypesInLegend,
    isSelectable: true,
  },
  materialization: {
    title: 'Materialization type',
    titleIcon: RyeconFile,
    description: 'The strategy for building the dbt models in your data platform',
    colorMap: materializationTypeColors,
    options: materializationTypes,
    isSelectable: true,
  },
  status: {
    title: 'Latest status',
    titleIcon: RyeconActivity,
    description:
      'The status from the latest execution of the resource in the current environment',
    colorMap: nodeStatusColors,
    options: statusLegendTypes,
    groupedLegendItems: [
      { label: 'Success / Pass', color: 'bg-fgVizEmerald', keys: ['success', 'pass'] },
      { label: 'Warn', color: 'bg-fgVizAmber', keys: ['warn'] },
      { label: 'Error', color: 'bg-fgVizOrange', keys: ['error'] },
      { label: 'Fail', color: 'bg-fgVizRed', keys: ['fail'] },
      { label: 'Reused', color: 'bg-fgVizTeal', keys: ['reused'] },
      {
        label: 'Skipped / Unconfigured / Outdated / Unknown',
        color: 'bg-fgVizNeutral',
        keys: ['skipped', 'unconfigured', 'outdated', 'unknown'],
      },
    ],
    isSelectable: true,
  },
  testStatus: {
    title: 'Test status',
    titleIcon: RyeconTest,
    description:
      'The status from the latest execution of the tests that ran against this resource',
    colorMap: nodeStatusColors,
    options: testStatuses,
    isSelectable: true,
  },
  modelingLayer: {
    title: 'Model layer',
    titleIcon: RyeconModelLayers,
    description: (
      <>
        The modeling layer that the model belongs to, according to dbt Labs{' '}
        <Link
          isInternal={false}
          to="https://docs.getdbt.com/best-practices/how-we-structure/1-guide-overview#guide-structure-overview"
          onClick={(e) => e.stopPropagation()}
          shouldOpenNewTab
        >
          best practices
        </Link>
      </>
    ),
    colorMap: modelLayerColors,
    options: modelLayers,
    isSelectable: true,
  },
  transformation: {
    title: 'Evolution',
    titleIcon: RyeconPath,
    description: 'The type of changes applied to the column data over time',
    colorMap: transformationTypeColors,
    options: transformationTypes,
    isSelectable: true,
  },
  usageQueries: {
    title: 'Consumption query history',
    titleIcon: RyeconNodeQuery,
    description:
      'The number of consumed queries against this resource in a given time period',
    colorMap: queryCountsColors,
    options: queryCountBuckets,
    tagline: 'Last 30 Days',
    isSelectable: true,
  },
  usageQueriesNudge: {
    title: 'Consumption query history (New)',
    titleIcon: RyeconNodeQuery,
    // TODO: add in link to usage queries docs once available
    description: (
      <>
        {' '}
        <Link
          isInternal={false}
          to="https://docs.getdbt.com/docs/collaborate/model-query-history#enable-query-history-in-dbt-cloud"
          onClick={(e) => e.stopPropagation()}
          shouldOpenNewTab
        >
          Requires setup
        </Link>{' '}
        in order to use this lens
      </>
    ),
    colorMap: queryCountsColors,
    options: queryCountBuckets,
    tagline: 'Last 30 Days',
    isSelectable: false,
  },
  cost: {
    title: 'Execution cost',
    titleIcon: RyeconProject,
    description: 'The cost to execute the resource in a given time period',
    colorMap: costColors,
    options: costBuckets,
    isSelectable: true,
  },
};

export const LensLegend: Subcomponent<LensLegendProps> = ({
  className,
  initialLens,
  onErrorFooterClick,
}: LensLegendProps) => {
  const { lens, setLens, lensContext } = useDagContext();
  const currentLens = lens || 'default';
  const lensState = lensContext.lensState?.get(currentLens);

  useEffect(() => {
    if (initialLens) {
      setLens(initialLens);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <span className="flex">
      <LensLegendDisplay {...LensLegends[currentLens]} className={className} />
      {lensState && lensState.state === 'error' && (
        <LensLegendErrorFooter
          message={lensState.description}
          onClick={onErrorFooterClick}
        />
      )}
      {lensState && lensState.state === 'info' && (
        <LensLegendInfoFooter
          message={lensState.description}
          tooltip={lensState.descriptionDetails}
        />
      )}
    </span>
  );
};

LensLegend.displayName = 'LensLegend';

interface LensLegendErrorFooterProps {
  message: string;
  onClick?(e: React.MouseEvent<HTMLAnchorElement>): void;
}

const LensLegendErrorFooter: React.FC<LensLegendErrorFooterProps> = ({
  message,
  onClick,
}) => {
  return (
    <div
      className={twMerge(
        'pointer-events-auto flex flex-wrap content-center justify-center align-middle',
        'box-border border border-borderDangerMuted bg-bgDangerMuted text-fgMain',
        'min-h-8 w-fit items-center rounded px-2 py-1',
      )}
      data-testid="lens-legend-error-footer"
    >
      <span className="flex items-center pr-2 text-xs">
        <Icon className="text-fgDanger" ryecon={RyeconDuotoneInfo} size="xs" />
        <span className="pl-1 pr-2 text-xs text-fgDanger">{message}</span>
        <Link
          testId="error-link"
          isInternal={false}
          colorVariant="error"
          onClick={onClick}
          to="#"
        >
          View Error
        </Link>
      </span>
    </div>
  );
};

interface LensLegendInfoFooterProps {
  tooltip: string;
  message: string;
}

const LensLegendInfoFooter: React.FC<LensLegendInfoFooterProps> = ({
  tooltip,
  message,
}) => {
  return (
    <div
      className={twMerge(
        'pointer-events-auto flex flex-wrap content-center justify-center align-middle',
        'box-border border border-borderInfoMuted bg-bgInfoMuted text-fgMain',
        'min-h-8 w-fit items-center rounded px-2 py-1',
      )}
      data-testid="lens-legend-info-footer"
    >
      <span className="flex items-center pr-2 text-xs">
        <Tooltip content={tooltip} placement="top-end" className="flex items-center">
          <Icon className="text-fgInfo" ryecon={RyeconDuotoneInfo} size="xs" />
        </Tooltip>
        <span className="pl-1 pr-2 text-xs text-fgInfo">{message}</span>
      </span>
    </div>
  );
};
